import React, { useEffect, useRef, useState } from 'react'
import rawAxios from 'axios'
import api from '../../../config/axios'
import {
  Page, Topbar, Brand, Grid, SideCol, SecGrid, Card, Pad, PanelTitle, Field, Input, SearchWrap, SearchResults,
  Divider, Button, Hint, Result, Empty, RHead, Verdict, ScoreBand,
  Gauge, ScoreMeta, Section, KV, KVStack, ChipNew, Pends, Pend, Socio, Badge, Alerts, Footnote, Skel,
  PrintStyles, PrintBtn, PrintOnly,
} from './styles'
import { CompanyItem, CreditoResponse, ResultadoCredito, RecomendacaoPrazo } from './types'

// Integração-quod (orchestrator ordeno-operacional) — chamada direta, sem ponte no backend
const CREDITO_URL =
  process.env.REACT_APP_CHAT_BASE_URL || 'https://ordeno-operacional-hjk3p7rq3q-rj.a.run.app'
const CREDITO_API_KEY = process.env.REACT_APP_CREDITO_API_KEY || ''

const brl = (v?: number) =>
  (v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
const onlyDigits = (s: string) => (s || '').replace(/\D/g, '')
// Máscara de CPF (11) ou CNPJ (14) conforme a quantidade de dígitos
const maskDoc = (s: string) => {
  const d = onlyDigits(s).slice(0, 14)
  if (d.length <= 11) {
    return d
      .replace(/^(\d{3})(\d)/, '$1.$2')
      .replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
      .replace(/\.(\d{3})(\d)/, '.$1-$2')
  }
  return d
    .replace(/^(\d{2})(\d)/, '$1.$2')
    .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1/$2')
    .replace(/(\d{4})(\d)/, '$1-$2')
}
const verdictClass = (r?: string) => r || 'Reprovado'

function GaugeSvg({ score }: { score: number }) {
  const pct = Math.max(0, Math.min(1, score / 1000))
  const a = Math.PI * (1 - pct)
  const cx = 95, cy = 100, r = 80
  const x = (cx + r * Math.cos(a)).toFixed(2)
  const y = (cy - r * Math.sin(a)).toFixed(2)
  const col = score >= 700 ? '#047857' : score >= 600 ? '#B45309' : '#DC2626'
  return (
    <Gauge>
      <svg width="190" height="112" viewBox="0 0 190 112">
        <path d="M15 100 A80 80 0 0 1 175 100" fill="none" stroke="#F3F4F6" strokeWidth="13" strokeLinecap="round" />
        {pct > 0 && (
          <path d={`M15 100 A80 80 0 0 1 ${x} ${y}`} fill="none" stroke={col} strokeWidth="13" strokeLinecap="round" />
        )}
      </svg>
      <div className="num">
        <b style={{ color: col }}>{score}</b>
        <span>de 1000</span>
      </div>
    </Gauge>
  )
}

const CreditAnalysis: React.FC = () => {
  // busca de cliente (autocomplete)
  const [clientQuery, setClientQuery] = useState('')
  const [clientResults, setClientResults] = useState<CompanyItem[]>([])
  const [showResults, setShowResults] = useState(false)
  const [searching, setSearching] = useState(false)
  const [selectedClient, setSelectedClient] = useState<CompanyItem | null>(null)
  const searchRef = useRef<HTMLDivElement>(null)

  // CNPJ avulso
  const [cnpjInput, setCnpjInput] = useState('')
  const [cnpjError, setCnpjError] = useState(false)

  // resultado
  const [loading, setLoading] = useState(false)
  const [errorMsg, setErrorMsg] = useState('')
  const [credito, setCredito] = useState<ResultadoCredito | null>(null)
  const [recomendacao, setRecomendacao] = useState<RecomendacaoPrazo | null>(null)

  // Busca conforme digita (debounce 300ms) — traz só o que casa com o texto
  useEffect(() => {
    if (selectedClient && clientQuery === selectedClient.companyName) return
    const q = clientQuery.trim()
    if (!q) {
      setClientResults([])
      return
    }
    setSearching(true)
    const t = setTimeout(() => {
      api
        .get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/companies/companies`, {
          params: { query: q, companyType: 'Cliente' },
        })
        .then((res) => {
          if (Array.isArray(res.data)) setClientResults(res.data.slice(0, 30))
          setShowResults(true)
        })
        .catch((err) => console.error('[credito] busca de clientes falhou', err))
        .finally(() => setSearching(false))
    }, 300)
    return () => clearTimeout(t)
  }, [clientQuery, selectedClient])

  // Fecha o dropdown ao clicar fora
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) setShowResults(false)
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [])

  function pickClient(c: CompanyItem) {
    setSelectedClient(c)
    setClientQuery(c.companyName)
    setShowResults(false)
    setCnpjInput('')
    setCnpjError(false)
  }

  async function runAnalysis(cnpjRaw: string) {
    const digits = onlyDigits(cnpjRaw)
    if (digits.length !== 14 && digits.length !== 11) {
      setCnpjError(true)
      return
    }
    setCnpjError(false)
    setLoading(true)
    setErrorMsg('')
    setCredito(null)
    setRecomendacao(null)
    try {
      const res = await rawAxios.get<CreditoResponse>(`${CREDITO_URL}/credito/${digits}`, {
        params: { valor_pedido: 0 },
        headers: CREDITO_API_KEY ? { 'X-API-Key': CREDITO_API_KEY } : {},
        timeout: 60000,
      })
      if (!res.data?.sucesso || !res.data.credito) {
        setErrorMsg(res.data?.erro || 'Não foi possível consultar este CNPJ na Quod.')
      } else {
        setCredito(res.data.credito)
        setRecomendacao(res.data.recomendacao || null)
      }
    } catch (err: any) {
      setErrorMsg(
        err?.response?.data?.detail ||
          err?.response?.data?.erro ||
          err?.message ||
          'Erro ao consultar a integração-quod.',
      )
    } finally {
      setLoading(false)
    }
  }

  function handleRun() {
    if (selectedClient?.cnpj) runAnalysis(selectedClient.cnpj)
    else runAnalysis(cnpjInput)
  }

  // Abre a análise já pronta a partir de ?doc=... (usado pelo botão "Analisar" dos sócios,
  // que abre esta tela em outra aba). Roda uma vez ao montar.
  useEffect(() => {
    const doc = new URLSearchParams(window.location.search).get('doc')
    if (!doc) return
    const digits = onlyDigits(doc)
    if (digits.length === 11 || digits.length === 14) {
      setCnpjInput(maskDoc(digits))
      runAnalysis(digits)
    }
  }, [])


  const kv = (k: string, v?: React.ReactNode, isNew?: boolean) =>
    v ? (
      <div>
        <div className="k">
          {k} {isNew && <ChipNew>novo</ChipNew>}
        </div>
        <div className="val">{v}</div>
      </div>
    ) : null

  const pendCard = (label: string, qtd: number, valor: number | null, isNew?: boolean) => {
    const state = qtd > 0 ? 'hit' : qtd === 0 ? 'clean' : ''
    return (
      <Pend $state={state as 'hit' | 'clean' | ''}>
        <div className="t">
          <span>{label}</span>
          {isNew && <ChipNew>novo</ChipNew>}
        </div>
        <div className="q">{qtd}</div>
        <div className="vv">{valor != null ? (valor > 0 ? brl(valor) : '—') : ' '}</div>
      </Pend>
    )
  }

  return (
    <Page className="credito-root">
      <PrintStyles />
      <Topbar className="no-print">
        <Brand>
          <div>
            <h1>Análise de Crédito</h1>
            <div className="sub">ordeno · integração Quod</div>
          </div>
        </Brand>
        {credito && (
          <PrintBtn className="no-print" onClick={() => window.print()}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M6 9V2h12v7" />
              <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
              <path d="M6 14h12v8H6z" />
            </svg>
            Imprimir
          </PrintBtn>
        )}
      </Topbar>

      <Grid className="credito-grid">
        {/* Seletor */}
        <SideCol className="no-print">
        <Card>
          <Pad>
            <PanelTitle>Consultar cliente</PanelTitle>

            <Field htmlFor="clientsearch">Cliente cadastrado</Field>
            <SearchWrap ref={searchRef}>
              <Input
                id="clientsearch"
                placeholder="Digite o nome do cliente…"
                autoComplete="off"
                value={clientQuery}
                onFocus={() => clientResults.length && setShowResults(true)}
                onChange={(e) => {
                  setClientQuery(e.target.value)
                  setSelectedClient(null)
                }}
              />
              {showResults && clientQuery.trim() && (
                <SearchResults>
                  {clientResults.length ? (
                    clientResults.map((c) => (
                      <li key={c.id} onClick={() => pickClient(c)}>
                        <span className="nm">{c.companyName}</span>
                        {c.cnpj && <span className="doc">{c.cnpj}</span>}
                      </li>
                    ))
                  ) : (
                    <div className="none">{searching ? 'Buscando…' : 'Nenhum cliente encontrado.'}</div>
                  )}
                </SearchResults>
              )}
            </SearchWrap>

            <Divider>ou</Divider>

            <Field htmlFor="cnpj">Novo CNPJ / CPF</Field>
            <Input
              id="cnpj"
              placeholder="CNPJ ou CPF"
              inputMode="numeric"
              autoComplete="off"
              value={cnpjInput}
              data-error={cnpjError}
              onChange={(e) => {
                setCnpjInput(maskDoc(e.target.value))
                setSelectedClient(null)
                setClientQuery('')
                setCnpjError(false)
              }}
            />

            <div style={{ height: 14 }} />
            <Button onClick={handleRun} disabled={loading}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                <circle cx="11" cy="11" r="7" />
                <path d="m20 20-3.5-3.5" />
              </svg>
              {loading ? 'Consultando…' : 'Rodar análise'}
            </Button>

            <Hint>
              Chamada direta à <code>GET /credito/{'{cnpj}'}</code> da integração-quod. Nada é salvo nesta fase.
            </Hint>
          </Pad>
        </Card>
        </SideCol>

        {/* Resultado */}
        <Card className="credito-result">
          <Result>
            {loading ? (
              <div style={{ padding: 18 }}>
                <Skel style={{ height: 26, width: '60%', marginBottom: 10 }} />
                <Skel style={{ height: 14, width: '35%', marginBottom: 22 }} />
                <Skel style={{ height: 96, width: '100%', marginBottom: 16 }} />
                <Skel style={{ height: 120, width: '100%' }} />
              </div>
            ) : errorMsg ? (
              <Empty data-error="true">
                <svg width="42" height="42" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
                  <circle cx="12" cy="12" r="9" />
                  <path d="M12 8v5M12 16h.01" />
                </svg>
                <div>{errorMsg}</div>
              </Empty>
            ) : !credito ? (
              <Empty>
                <svg width="46" height="46" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
                  <path d="M3 3v18h18" />
                  <path d="m7 14 3-4 3 2 4-6" />
                </svg>
                <div>
                  Busque um cliente ou informe um CNPJ
                  <br />e rode a análise para ver o resultado.
                </div>
              </Empty>
            ) : (
              <>
                <PrintOnly>
                  <div className="pt-title">Análise de Crédito</div>
                  <div className="pt-sub">
                    Ordeno · integração Quod · impresso em {new Date().toLocaleString('pt-BR')}
                  </div>
                </PrintOnly>
                <RHead className="credito-rhead">
                  <div>
                    <h2>{credito.razao_social || 'CNPJ ' + credito.cnpj}</h2>
                    {credito.nome_fantasia && <div className="fant">{credito.nome_fantasia}</div>}
                    <div className="cnpj">
                      {credito.tipo === 'PF' ? 'CPF ' : ''}{maskDoc(credito.cnpj)} · consulta {new Date().toLocaleDateString('pt-BR')}
                    </div>
                  </div>
                  <Verdict $v={verdictClass(credito.resultado_analise)}>{credito.resultado_analise}</Verdict>
                </RHead>

                <ScoreBand className="credito-scoreband">
                  <GaugeSvg score={credito.score_pj || 0} />
                  <ScoreMeta>
                    {credito.score_reason && <div className="reason">{credito.score_reason}</div>}
                    {credito.motivo && <div className="reason">Motivo: {credito.motivo}</div>}
                    {recomendacao && (
                      <div className="reco">
                        <div className="lab">Prazo recomendado</div>
                        <div className="prazo">{recomendacao.prazo_recomendado}</div>
                        <div className="risk">
                          Risco: <b>{(recomendacao.risco || '').replace('_', ' ')}</b>
                          {credito.recomendacao_credito ? ` · ${credito.recomendacao_credito}` : ''}
                        </div>
                      </div>
                    )}
                  </ScoreMeta>
                </ScoreBand>

                <SecGrid className="credito-secgrid">
                <Section>
                  <h3>Empresa</h3>
                  <KV>
                    {kv('Situação na Receita', credito.situacao_receita, true)}
                    {kv('Nascimento', credito.data_nascimento)}
                    {kv(
                      'Fundação',
                      credito.data_fundacao
                        ? `${credito.data_fundacao}${credito.tempo_atividade_anos ? ` · ${credito.tempo_atividade_anos} anos` : ''}`
                        : undefined,
                    )}
                    {kv(
                      'Última alteração',
                      credito.data_ultima_alteracao
                        ? `${credito.data_ultima_alteracao}${credito.tempo_desde_ultima_alteracao_anos ? ` · ${credito.tempo_desde_ultima_alteracao_anos} anos` : ''}`
                        : undefined,
                    )}
                    {kv('Faixa de faturamento', credito.faixa_faturamento)}
                    {kv('Capital social', credito.capital_social ? brl(credito.capital_social) : undefined)}
                    {kv('Atividade principal', credito.atividade_principal)}
                    {kv('Atividade secundária', credito.atividade_secundaria)}
                    {kv(
                      'Localização',
                      credito.endereco_cidade ? `${credito.endereco_cidade}/${credito.endereco_estado}` : undefined,
                    )}
                    {kv(
                      'Consultas recentes (60d)',
                      credito.consultas_recentes != null ? String(credito.consultas_recentes) : undefined,
                      true,
                    )}
                    {kv(
                      'Pontualidade de pagamento',
                      credito.pontualidade_pagamento != null ? `${credito.pontualidade_pagamento}%` : undefined,
                    )}
                  </KV>
                </Section>

                <Section>
                  <h3>Contato &amp; Endereço</h3>
                  {credito.email || (credito.telefones && credito.telefones.length) || credito.endereco_completo ? (
                    <KVStack>
                      {kv('E-mail', credito.email)}
                      {kv(
                        'Telefones',
                        credito.telefones && credito.telefones.length ? credito.telefones.join('  ·  ') : undefined,
                      )}
                      {kv('Endereço', credito.endereco_completo)}
                    </KVStack>
                  ) : (
                    <Hint>Contato/endereço não retornado para este CNPJ.</Hint>
                  )}
                </Section>

                <Section>
                  <h3>
                    Pendências{' '}
                    {credito.total_pendencias > 0 ? (
                      <span className="n">
                        · {credito.total_pendencias} ({brl(credito.total_pendencias_valor)})
                      </span>
                    ) : (
                      <span className="g">· nenhuma</span>
                    )}
                  </h3>
                  <Pends>
                    {pendCard('Inadimplências', credito.inadimplencias_qtd, credito.inadimplencias_valor)}
                    {pendCard('Ações judiciais', credito.acoes_judiciais_qtd, credito.acoes_judiciais_valor)}
                    {pendCard('Protestos', credito.protestos_qtd, credito.protestos_valor)}
                    {pendCard('Cheques s/ fundo', credito.cheques_sem_fundo_qtd, null)}
                    {pendCard('Falências', credito.falencias_qtd, null)}
                    {pendCard('Recuperações', credito.recuperacoes_qtd, null)}
                  </Pends>

                  {credito.protestos && credito.protestos.length > 0 && (
                    <div style={{ marginTop: 12 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7785', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 6 }}>
                        Protestos por cartório
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 260, overflowY: 'auto' }}>
                        {credito.protestos.map((p, i) => (
                          <div
                            key={i}
                            style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'baseline', border: '1px solid #e6eaee', borderRadius: 8, padding: '8px 10px' }}
                          >
                            <div>
                              <div style={{ fontSize: 13, fontWeight: 500, color: '#1f2933' }}>
                                {[p.cidade, p.uf].filter(Boolean).join('/') || 'Local não informado'}
                                {p.quantidade ? (
                                  <span style={{ color: '#6b7785', fontWeight: 400 }}> · {p.quantidade} protesto(s)</span>
                                ) : null}
                              </div>
                              {p.cartorio && <div style={{ fontSize: 11.5, color: '#8a949e' }}>{p.cartorio}</div>}
                            </div>
                            {p.valor != null && (
                              <div style={{ fontSize: 13, fontWeight: 600, color: '#b23b3b', whiteSpace: 'nowrap' }}>{brl(p.valor)}</div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </Section>

                {credito.tipo !== 'PF' && (
                <Section>
                  <h3>Sócios</h3>
                  {credito.socios && credito.socios.length ? (
                    credito.socios.map((s, i) => {
                      const temRestricao = !!s.restricao || (s.pendencias || 0) > 0
                      const detalhes = [
                        s.cpf_cnpj,
                        s.percentual ? `${s.percentual}%` : '',
                        s.data_entrada ? `desde ${s.data_entrada}` : '',
                      ]
                        .filter(Boolean)
                        .join('  ·  ')
                      return (
                        <Socio key={i}>
                          <div>
                            <div className="nm">{s.nome}</div>
                            <div className="dc">{detalhes}</div>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Badge $bad={temRestricao}>
                              {temRestricao ? 'com restrição' : 'sem restrição'}
                            </Badge>
                            {onlyDigits(s.cpf_cnpj).length >= 11 && (
                              <button
                                type="button"
                                className="no-print"
                                onClick={() =>
                                  window.open(
                                    `${window.location.pathname}?doc=${onlyDigits(s.cpf_cnpj)}`,
                                    '_blank',
                                    'noopener',
                                  )
                                }
                                title="Abrir a análise de crédito deste sócio em nova aba"
                                style={{
                                  border: '1px solid #0090D3',
                                  color: '#0090D3',
                                  background: 'transparent',
                                  borderRadius: 8,
                                  padding: '4px 10px',
                                  fontSize: 12,
                                  fontWeight: 500,
                                  cursor: 'pointer',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                Analisar ↗
                              </button>
                            )}
                          </div>
                        </Socio>
                      )
                    })
                  ) : (
                    <Hint>Sem sócios retornados.</Hint>
                  )}
                </Section>
                )}

                <Section>
                  <h3>Alertas &amp; decisão</h3>
                  <KVStack>
                    {kv('Decisão', credito.resultado_analise, true)}
                    {kv('Prazo recomendado', recomendacao?.prazo_recomendado)}
                    {kv('Risco', recomendacao?.risco ? recomendacao.risco.replace(/_/g, ' ') : undefined)}
                    {kv('Recomendação de crédito', credito.recomendacao_credito)}
                    {kv('Justificativa', recomendacao?.justificativa)}
                    {kv('Motivo da decisão', credito.motivo)}
                    {kv(
                      'Pontualidade de pagamento',
                      credito.pontualidade_pagamento != null ? `${credito.pontualidade_pagamento}%` : undefined,
                    )}
                    {kv(
                      'Total de pendências',
                      credito.total_pendencias > 0
                        ? `${credito.total_pendencias} · ${brl(credito.total_pendencias_valor)}`
                        : 'nenhuma',
                      true,
                    )}
                  </KVStack>
                  <Alerts>
                    {recomendacao?.alertas && recomendacao.alertas.length ? (
                      recomendacao.alertas.map((a, i) => (
                        <div className="alert" key={i}>
                          <span className="ic">▲</span>
                          <span>{a}</span>
                        </div>
                      ))
                    ) : (
                      <Hint>Sem alertas.</Hint>
                    )}
                  </Alerts>
                </Section>
                </SecGrid>

                <Footnote>
                  <span>◆</span>
                  <span>
                    Consulta em tempo real via <code>GET /credito/{'{cnpj}'}</code> (integração-quod). Campos{' '}
                    <ChipNew>novo</ChipNew> aparecem quando o parser passar a expô-los.
                  </span>
                </Footnote>
              </>
            )}
          </Result>
        </Card>
      </Grid>
    </Page>
  )
}

export default CreditAnalysis
